<script setup lang="ts">
/**
 * Plays a deck: the checkbox model (cards leave the queue when done) or the flowchart
 * model (a flow graph decides what happens). State and behaviour live in
 * useDeckPlayerShell; the media views and admin testing aids are child components.
 */
import type { FlowContext, FlowResult } from '../../../../../shared/player-contracts/src'
import coinsImage from '../../assets/coins.png'
import pearlImage from '../../assets/pearl.svg'
import type { DeckCardInput } from '../../deck/types'
import type { DeckTemplateSettings } from '../../deck/templateSettings'
import Flashcard from '../../flashcard/Flashcard.vue'
import { useDeckPlayerShell } from '../composables/useDeckPlayerShell'
import type { FlowGraphV1 } from '../flowchart/flowGraph'
import type { MediaMaterial } from '../flowchart/runtime/media'
import type { DeckPlayerModel, DeckPlayerShortcodeProps } from '../types'
import DeckPlayerCardStage from './DeckPlayerCardStage.vue'
import DeckPlayerCount from './DeckPlayerCount.vue'
import DeckPlayerProgressBar from './DeckPlayerProgressBar.vue'
import FloatingFlowCanvas from './shell/FloatingFlowCanvas.vue'
import FlowDebugPanel from './shell/FlowDebugPanel.vue'
import FlowSkipButton from './shell/FlowSkipButton.vue'
import MediaModal from './shell/MediaModal.vue'
import MediaSplit from './shell/MediaSplit.vue'
import MediaStandalone from './shell/MediaStandalone.vue'
import PreStartScreen from './shell/PreStartScreen.vue'

const props = withDefaults(
  defineProps<{
    deckId: string
    deckTitle?: string
    model: DeckPlayerModel
    cards: DeckCardInput[]

    /** Optional deck metadata (used by model="flowchart"). */
    deckCardIds?: string[]
    deckGroups?: Array<{ id?: string; title: string; cardIds: string[] }>
    flowGraph?: FlowGraphV1 | null

    /** Force verbose console logging (temporary debugging aid). */
    debug?: boolean

    /** REST context for progress POSTs (Host already has this from shortcode/widget props). */
    restRoot?: string
    nonce?: string

    /** If true, always show the completion UI (used when backend says deck is completed). */
    forceComplete?: boolean

    /** Completion policy: if false, deck cannot be restarted once fully completed. */
    allowReplayWhenCompleted?: boolean

    /** Completion policy: if false, retry button is disabled (e.g. cooldown lock). */
    retryAllowed?: boolean

    /** If true, emit completion events but do not render the built-in completion UI. */
    suppressCompletionUi?: boolean
    /** Editor preview helper: keep the last pulled card visible after flow end. */
    keepCardOnFlowEnd?: boolean

    /** Optional ISO datetime when retry becomes available again. */
    retryBlockedUntil?: string | null

    /** ISO datetime string — if set, flow continue is blocked until this time. */
    flowPropagationBlockedUntil?: string | null

    /** If true, do not POST progress events (useful for local-only retry sessions). */
    suppressProgressWrites?: boolean

    /** Optional deck-level template settings (future: persisted on the deck). */
    deckTemplate?: DeckTemplateSettings

    // initial progress from backend (only used if logged in)
    initialValues?: Record<string, boolean[]>

    /** Flowchart resume point: the phaseId the user last reached. If set, graphStart() skips to that phase node. */
    initialFlowPhaseId?: string | null

    // passthrough UI props
    playerProps?: DeckPlayerShortcodeProps

    // auth
    isLoggedIn?: boolean

    /** When true, graph execution pauses at every node waiting for stepSignal to increment. */
    manualStep?: boolean
    /** Increment this to advance one step in manual step mode. */
    stepSignal?: number
    /** Debug-only: milliseconds to pause between auto-advanced nodes (0 = instant). */
    debugStepDelay?: number
    /** When true, suppress the floating debug panel and flow canvas (e.g. inside the flowchart editor). */
    inEditor?: boolean
    /** Media materials from the course — used to resolve URLs for show-media nodes. */
    mediaMaterials?: MediaMaterial[]
    /** Course data for phase getters (stored answers of the course's other levels). */
    flowContext?: FlowContext | null
    /**
     * Called when a standalone or split media node pauses the flow.
     * Receives the media info and a `resume` callback — the caller can store `resume`
     * and invoke it (e.g. from a "Fortfahren" button in the course UI) to advance the flow.
     */
    onMediaPause?: (
      info: { displayMode: string; mediaType: string; mediaName: string; mediaId: string },
      resume: () => void,
    ) => void
  }>(),
  {
    deckTitle: '',
    initialValues: () => ({}),
    playerProps: () => ({}),
    deckTemplate: () => ({}),
    isLoggedIn: false,
    deckCardIds: () => [],
    deckGroups: () => [],
    flowGraph: null,
    suppressProgressWrites: false,
    forceComplete: false,
    allowReplayWhenCompleted: true,
    retryAllowed: true,
    suppressCompletionUi: false,
    keepCardOnFlowEnd: false,
    retryBlockedUntil: null,
    flowPropagationBlockedUntil: null,
    restRoot: undefined,
    nonce: undefined,
    debug: false,
    inEditor: false,
  },
)

const emit = defineEmits<{
  (e: 'card-removed', payload: { cardId: string }): void
  (e: 'retry', payload: { deckId: string }): void
  (e: 'completed', payload: { deckId: string }): void
  (
    e: 'flowchart-step-changed',
    payload: { nodeId: string | null; needsUserInteraction?: boolean },
  ): void
  (e: 'flow-end', payload: { deckId: string; result: FlowResult }): void
  (e: 'flow-continue', payload: { deckId: string }): void
  (e: 'flow-reset', payload: { deckId: string }): void
  (e: 'phase-interlude', payload: { deckId: string; phaseId: string | null }): void
  (
    e: 'show-media',
    payload: { mediaType: 'image' | 'video' | 'audio' | 'pdf'; mediaName: string; mediaId: string },
  ): void
  (e: 'awaiting-start'): void
}>()

const player = useDeckPlayerShell(props, emit)
const {
  t,
  playerEl: deckplayerEl,
  rewardsVisible,
  rewardPearls,
  rewardPoints,
  handleInteraction,
  handleFlowContinue,
  propagationBlockedText,
} = player
const {
  showingMedia: graphShowingMedia,
  awaitingStart: graphAwaitingStart,
  currentCardId: graphCurrentCardId,
  awaitingAnswer: graphAwaitingAnswer,
} = player.state
const resolvedMedia = player.resolvedMedia
const {
  total,
  completed,
  modelError,
  isFlowEnd,
  isPhaseInterlude,
  showFlowNextButton,
  flowPropagationBlocked,
} = player.progress
const {
  ui,
  patchedCard,
  frameWidthPx,
  frameEl,
  effectiveScale,
  scaledOuterStyle,
  flashcardPresentation,
  showAnyHeader,
} = player.presentation
const { handleCardClick, answerCard } = player.runner

defineExpose({
  stepForward: player.runner.stepForward,
  startGraph: player.startFlow,
  graphNeedsUserInteraction: player.state.needsUserInteraction,
  graphRegisters: player.state.registers,
  graphActivePhaseId: player.state.activePhaseId,
  graphActiveGroupId: player.state.activeGroupId,
  graphDoneCardIds: player.state.doneCardIds,
  debugState: player.runner.debugState,
})
</script>

<template>
  <section
    ref="deckplayerEl"
    class="deckplayer"
    :class="{
      'deckplayer--pre-start': graphAwaitingStart,
      'deckplayer--full-media':
        graphShowingMedia?.displayMode === 'standalone' ||
        graphShowingMedia?.displayMode === 'split',
    }"
    :style="{ '--pfc-deckplayer-width': `${frameWidthPx}px` }"
  >
    <div v-if="modelError" class="error">{{ modelError }}</div>

    <template v-else>
      <div ref="frameEl" class="frame">
        <div v-if="rewardsVisible" class="deckplayer__rewards">
          <div v-if="rewardPearls !== null" class="deckplayer__reward deckplayer__reward--pearls">
            <img
              :src="pearlImage"
              class="deckplayer__reward-img deckplayer__reward-img--pearl"
              aria-hidden="true"
              alt=""
            />
            <span class="deckplayer__reward-count">{{ rewardPearls }}</span>
          </div>
          <div v-if="rewardPoints !== null" class="deckplayer__reward deckplayer__reward--coins">
            <img
              :src="coinsImage"
              class="deckplayer__reward-img deckplayer__reward-img--coins"
              aria-hidden="true"
              alt=""
            />
            <span class="deckplayer__reward-count">{{ rewardPoints }}</span>
          </div>
        </div>

        <header v-if="showAnyHeader" class="header">
          <div v-if="ui.showDeckTitle" class="title">{{ props.deckTitle }}</div>
          <DeckPlayerCount
            v-if="
              ui.showCount &&
              !isPhaseInterlude &&
              (model !== 'flowchart' || graphCurrentCardId !== null)
            "
            :completed="completed"
            :total="total"
          />
          <DeckPlayerProgressBar
            v-if="
              ui.showProgressBar &&
              !isPhaseInterlude &&
              (props.model !== 'flowchart' || graphCurrentCardId !== null)
            "
            :completed="completed"
            :total="total"
          />
          <div v-if="ui.showLoginHint && !props.isLoggedIn" class="hint">
            {{ t('deckPlayer.shell.loginHint') }}
          </div>
        </header>

        <!-- Phase interlude: checkpoint reached mid-flow -->
        <div v-if="isPhaseInterlude" class="completion flow-end">
          <div class="completion__row">
            <div class="completion__text">{{ t('deckPlayer.shell.flowPhaseCompleted') }}</div>
          </div>
          <div v-if="flowPropagationBlocked" class="completion__hint">
            {{ propagationBlockedText }}
          </div>
          <div v-if="showFlowNextButton && !flowPropagationBlocked" class="flow-end__actions">
            <button type="button" class="pl-btn pl-btn--primary" @click="handleFlowContinue">
              {{ t('deckPlayer.shell.flowContinue') }}
            </button>
          </div>
        </div>

        <!-- Pre-start screen: shown on first load before graphStart() -->
        <PreStartScreen v-else-if="graphAwaitingStart" />

        <!-- Standalone: inline where the card would be, no background -->
        <MediaStandalone
          v-else-if="graphShowingMedia && resolvedMedia?.displayMode === 'standalone'"
          :resolved-media="resolvedMedia"
        />

        <!-- Split: two panes inline, no background -->
        <MediaSplit
          v-else-if="graphShowingMedia && resolvedMedia?.displayMode === 'split'"
          :resolved-media="resolvedMedia"
        />

        <DeckPlayerCardStage
          v-else-if="
            patchedCard &&
            (!isFlowEnd || props.keepCardOnFlowEnd) &&
            !isPhaseInterlude &&
            (!graphShowingMedia || resolvedMedia?.displayMode === 'modal')
          "
          :card-id="patchedCard.id"
          :transition="ui.cardChangeAnimation"
        >
          <div
            class="card-scale-outer"
            :class="{ 'card-scale-outer--clickable': model === 'flowchart' }"
            :data-pfc-card-id="patchedCard.id"
            :data-pfc-scale="effectiveScale"
            :style="scaledOuterStyle"
            @click="handleCardClick"
          >
            <div v-if="model === 'flowchart'" class="pfc-flowchart-overlay" />
            <div class="card-scale-inner">
              <Flashcard
                v-if="flashcardPresentation"
                v-bind="flashcardPresentation"
                @element-interaction="handleInteraction"
              />
            </div>
          </div>
        </DeckPlayerCardStage>

        <!-- "Ask: known?" node: the learner rates the card -->
        <div
          v-if="graphAwaitingAnswer && patchedCard && !isPhaseInterlude && !graphShowingMedia"
          class="answer-buttons"
        >
          <button type="button" class="answer-btn answer-btn--unknown" @click="answerCard(false)">
            ✗ {{ t('deckPlayer.shell.answerUnknown') }}
          </button>
          <button type="button" class="answer-btn answer-btn--known" @click="answerCard(true)">
            ✓ {{ t('deckPlayer.shell.answerKnown') }}
          </button>
        </div>
      </div>
    </template>
  </section>

  <MediaModal />
  <FlowDebugPanel />
  <FloatingFlowCanvas />
  <FlowSkipButton />
</template>

<style scoped>
/* Minimal by default: no outer box, no forced background. */
.deckplayer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: var(--pl-color-text, #0f172a);
}

.deckplayer,
.deckplayer :deep(*) {
  box-sizing: border-box !important;
}

.deckplayer {
  position: relative;
}

.deckplayer--full-media {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.deckplayer--full-media .frame {
  width: 100%;
  height: 100%;
  min-height: 0;
}

.deckplayer__rewards {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  pointer-events: none;
}

.deckplayer__reward {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
}

.deckplayer__reward-img {
  height: auto;
  flex-shrink: 0;
  display: block;
}

.deckplayer__reward-img--coins {
  width: 44px;
}

.deckplayer__reward-img--pearl {
  width: 17px;
  filter: drop-shadow(0 2px 3px rgba(9, 42, 64, 0.28));
}

.deckplayer__reward-count {
  min-width: 26px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #f5a623;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.2;
  text-align: center;
  white-space: nowrap;
}

.deckplayer__reward--pearls .deckplayer__reward-count {
  background: #8aa7bd;
}

.frame {
  width: min(100%, var(--pfc-deckplayer-width));
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.header {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 0.5rem 0.75rem;
  align-items: center;
  padding: 0.5rem 0;
}

.title {
  font-size: 1.05rem;
  font-weight: 650;
  color: var(--pl-color-text, #0f172a);
  line-height: 1.2;
}

.header :deep(.count) {
  justify-self: end;
}

.header :deep(.bar) {
  grid-column: 1 / -1;
}

.hint {
  grid-column: 1 / -1;
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
}

.card-scale-outer {
  display: block;
  position: relative;
}

.card-scale-outer--clickable {
  cursor: pointer;
}

.pfc-flowchart-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  cursor: pointer;
}

.card-scale-inner {
  display: block;
}

/* Over the lower edge of the card: a card scaled to fill the player leaves no room below. */
.answer-buttons {
  position: absolute;
  left: 50%;
  bottom: 1.25rem;
  z-index: 60;
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  width: max-content;
  max-width: calc(100% - 1rem);
  transform: translateX(-50%);
}

.answer-btn {
  min-width: 9.5rem;
  padding: 0.7rem 1.2rem;
  border: none;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.18);
  transition:
    transform 0.12s ease,
    filter 0.12s ease;
}

.answer-btn:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
}

.answer-btn:active {
  transform: scale(0.97);
}

.answer-btn--known {
  background: #16a34a;
}

.answer-btn--unknown {
  background: #dc2626;
}

/* Minimal flat: flatten button visuals just for Deckplayer. */
.deckplayer :deep(.pl-btn) {
  box-shadow: none;
}

.deckplayer :deep(.pl-btn:hover) {
  transform: none;
}

.deckplayer :deep(.pl-btn:active) {
  transform: none;
}

.error {
  padding: 0.75rem 0.9rem;
  border-radius: 0.75rem;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.completion {
  color: var(--pl-color-text, #0f172a);
  font-weight: 600;
}

@media (max-width: 520px) {
  .header {
    grid-template-columns: 1fr;
  }

  .header :deep(.count) {
    justify-self: start;
  }
}
</style>

<style scoped>
.completion {
  padding: 1.1rem 1rem;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(241, 245, 249, 0.55);
  border-radius: 14px;
}

.completion__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.completion__text {
  font-weight: 650;
}

.completion__hint {
  margin-top: 0.55rem;
  font-size: 0.9rem;
  color: #475569;
}

@media (max-width: 520px) {
  .completion__row {
    flex-direction: column;
    align-items: stretch;
  }

  .completion__text {
    text-align: center;
  }
}

.flow-end__actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.pl-btn--danger {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
  font-weight: 600;
}

.pl-btn--danger:hover {
  background: #fee2e2 !important;
  color: #991b1b !important;
  border-color: #f87171 !important;
}

.pl-btn--secondary {
  background: transparent;
  color: #94a3b8;
  border: 1px dashed #cbd5e1;
  font-size: 0.82rem;
  letter-spacing: 0.01em;
}

.pl-btn--secondary:hover {
  background: #f8fafc !important;
  color: #64748b !important;
  border-color: #94a3b8 !important;
  border-style: solid !important;
}
</style>
